import { useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api, type CatalogLineRow, type LineFamily, type MirrorPlan } from '../../lib/api';
import { Card, CardHeader, Spinner, ActionButton, Badge, Modal, EmptyState } from '../ui';
import { SaveStatus, useSaveState } from '../SaveStatus';
import { invalidateCatalogLabels } from '../../hooks/useCatalogLabels';

/**
 * The FP&A line hierarchy, as a tree you can rearrange.
 *
 * WHY THIS SCREEN EXISTS. The catalog shipped as a seeded taxonomy and there
 * was no way to touch it: "Services Income" from QuickBooks read as "Time &
 * Materials" with no way to rename it, nothing could be nested more than one
 * level, and a line registered under the wrong family — `revenue.var.*`
 * classified as cost from its code name while the accounts behind it were
 * income — was permanent. Every statement in the product takes its shape from
 * this table, so it has to be the operator's to arrange.
 *
 * WHAT IS NOT EDITABLE, AND WHY. The subtotal rows (gross margin, operating
 * profit, net profit, total fixed costs, the cash lines) are calculated by the
 * engine from the FAMILY each line declares. They can be renamed and reordered
 * — "Gross margin" to "Gross Profit" is a house-style decision — but they
 * cannot be reparented, reclassified or deleted, because deleting `gm` would
 * not remove gross margin from the statement; it would leave the engine
 * emitting a row with nothing to render it. They carry a lock.
 */

const FAMILIES: Array<{ key: LineFamily; label: string; hint: string }> = [
  { key: 'revenue', label: 'Revenue', hint: 'Above the gross margin line.' },
  { key: 'cogs', label: 'Cost of sales', hint: 'Deducted from revenue to give gross margin.' },
  { key: 'salary', label: 'Salaries', hint: 'Driven by the headcount model. Mapping QBO payroll here double-counts against the engine’s own salary line.' },
  { key: 'opex', label: 'Operating expense', hint: 'Below gross margin, above operating profit.' },
  { key: 'other_income', label: 'Other income', hint: 'Below operating profit.' },
  { key: 'other_expense', label: 'Other expense', hint: 'Below operating profit.' },
  { key: 'capex', label: 'Capital expenditure', hint: 'Cash out, but not an expense: it never touches gross margin, operating profit or net profit. It does reduce closing cash.' },
];

const FAMILY_SHORT: Record<string, string> = {
  revenue: 'revenue', cogs: 'COGS', salary: 'salary', opex: 'opex',
  other_income: 'other inc', other_expense: 'other exp', capex: 'capex',
  subtotal: 'subtotal', cash: 'cash', kpi: 'KPI',
};

/** Mirrors ENGINE_OWNED_CODES on the server. Kept in sync by the lock tooltip
 *  being wrong rather than by anything breaking, so the server enforces it too. */
const ENGINE_OWNED = new Set(['gm', 'op_profit', 'net_profit', 'cost.fixed.total', 'cash.collections', 'cash.closing']);

/**
 * Leaves the engine WRITES TO from drivers, rather than from budget items or a
 * QBO mapping. Ordinary rows in the statement; they are just not typed by
 * anyone, so the editor says what feeds them.
 *
 * These can be renamed and moved anywhere - Time & Materials belongs wherever
 * the chart of accounts puts that revenue, and only the operator knows where -
 * but not reclassified, retired or deleted. Mirrors ENGINE_FED_CODES.
 */
const ENGINE_FED: Record<string, string> = {
  'rev.tm': 'the capacity model: headcount × hours × utilisation × bill rate',
  'rev.ff': 'fixed-fee revenue contracts',
  'rev.sub': 'subscription contracts and MRR',
  'rev.pipeline': 'the HubSpot pipeline sync',
  'cost.salary': 'the headcount model: salary × loaded multiplier, plus comp events',
  'cost.var.labor': "the engine's variable labour calculation",
  'cost.var.materials': "the engine's materials and subcontractor calculation",
};

interface TreeNode extends CatalogLineRow { children: TreeNode[]; depth: number }

/** Whether a collapse state has ever been saved, so a first visit can start
 *  shut without that choice sticking as 'everything expanded' thereafter. */
function hasCollapseState(): boolean {
  try { return window.localStorage.getItem('fpa.catalog.collapsed') !== null; }
  catch { return false; }
}

function buildTree(rows: CatalogLineRow[]): TreeNode[] {
  const byParent = new Map<string | null, CatalogLineRow[]>();
  for (const r of rows) {
    const k = r.parent_code ?? null;
    (byParent.get(k) ?? byParent.set(k, []).get(k)!).push(r);
  }
  const sortLevel = (a: CatalogLineRow, b: CatalogLineRow) => a.sort_order - b.sort_order || a.label.localeCompare(b.label);
  const walk = (parent: string | null, depth: number): TreeNode[] =>
    (byParent.get(parent) ?? []).slice().sort(sortLevel)
      .map((r) => ({ ...r, depth, children: walk(r.code, depth + 1) }));
  return walk(null, 0);
}

function flatten(nodes: TreeNode[]): TreeNode[] {
  return nodes.flatMap((n) => [n, ...flatten(n.children)]);
}

/** Flatten, but stop at a collapsed group. */
function flattenVisible(nodes: TreeNode[], collapsed: Set<string>): TreeNode[] {
  return nodes.flatMap((n) => (collapsed.has(n.code) ? [n] : [n, ...flattenVisible(n.children, collapsed)]));
}

const descendantCodes = (n: TreeNode): string[] => n.children.flatMap((c) => [c.code, ...descendantCodes(c)]);

/**
 * A subtotal row cannot be dragged: its position in the maths is fixed and
 * the server refuses to reparent it, so offering the gesture would only
 * produce an error. Engine-FED leaves stay draggable - they move freely.
 */
const dragDisabled = (n: CatalogLineRow) => ENGINE_OWNED.has(n.code);

/**
 * Scroll the window when the pointer nears its edge mid-drag.
 *
 * Without this the gesture is only usable between rows that already share a
 * screen, and on a chart of accounts the destination is usually several
 * screens away - the exact objection to dragging in the first place.
 */
function autoScroll(clientY: number) {
  const margin = 90;
  const step = 18;
  if (clientY < margin) window.scrollBy({ top: -step });
  else if (clientY > window.innerHeight - margin) window.scrollBy({ top: step });
}

/** Where a dragged row would land relative to the row under the pointer. */
type DropWhere = 'before' | 'after' | 'inside';

/**
 * Read the drop intent from where in the row the pointer is: the top and bottom
 * sixths mean "between siblings", the middle means "inside this group".
 *
 * Splitting a row this way is what lets one gesture express both reordering and
 * reparenting. Without the middle band there is no way to say "put it in here"
 * for a group that is collapsed or empty, which is most of them on a chart of
 * accounts this size.
 */
function dropIntent(e: React.DragEvent, isGroup: boolean): DropWhere {
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const y = e.clientY - r.top;
  if (!isGroup) return y < r.height / 2 ? 'before' : 'after';
  if (y < r.height / 4) return 'before';
  if (y > (r.height * 3) / 4) return 'after';
  return 'inside';
}

export default function LineCatalogEditor({ isApprover }: { isApprover: boolean }) {
  const catQ = useQuery(() => api.fpa.catalog(), []);
  const treeQ = useQuery(() => api.fpa.qbo.accountTree(true), []);
  const save = useSaveState();
  const [adding, setAdding] = useState<{ parent: CatalogLineRow | null } | null>(null);
  const [mirror, setMirror] = useState(false);
  const [blockers, setBlockers] = useState<{ code: string; blockers: Array<{ kind: string; detail: string }> } | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  /**
   * Collapsed branches, remembered across reloads.
   *
   * WHY THIS IS PART OF DRAG AND DROP, not a nicety. Dragging only works when
   * the row and its destination are on screen together, and a mirrored chart
   * of accounts is 250 rows deep - 'Fixed costs' alone has around a hundred
   * direct children. Collapsing the branches you are not working in is what
   * makes the gesture possible at all, so groups start collapsed and you open
   * what you need.
   */
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const saved = window.localStorage.getItem('fpa.catalog.collapsed');
      if (saved) return new Set(JSON.parse(saved) as string[]);
    } catch { /* a corrupt or unavailable store just means the default */ }
    return new Set<string>();
  });
  const [dragCode, setDragCode] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ code: string; where: DropWhere } | null>(null);
  const [dropErr, setDropErr] = useState<string | null>(null);

  if (catQ.isLoading) return <Card><Spinner /></Card>;
  const all = catQ.data?.catalog ?? [];
  const rows = showInactive ? all : all.filter((r) => r.is_active);
  const tree = buildTree(rows);
  const flat = flatten(tree);
  const byCode = new Map(flat.map((n) => [n.code, n]));
  const groupCodes = flat.filter((n) => n.children.length > 0).map((n) => n.code);
  // First visit: everything shut, so the page opens as a short list of roots
  // rather than 250 rows. Saved state wins once there is any.
  const effectiveCollapsed = collapsed.size === 0 && !hasCollapseState() ? new Set(groupCodes) : collapsed;
  const visible = flattenVisible(tree, effectiveCollapsed);
  // The dragged line's subtree, once per render rather than once per row.
  const dragDescendants = dragCode && byCode.has(dragCode)
    ? new Set(descendantCodes(byCode.get(dragCode)!))
    : null;

  function persistCollapsed(next: Set<string>) {
    setCollapsed(next);
    try { window.localStorage.setItem('fpa.catalog.collapsed', JSON.stringify([...next])); }
    catch { /* non-fatal: the tree just reopens next time */ }
  }
  const toggle = (code: string) => {
    const next = new Set(effectiveCollapsed);
    if (next.has(code)) next.delete(code); else next.add(code);
    persistCollapsed(next);
  };

  // How many QBO accounts point at each line, and what they carry. A line with
  // no mapping and no items is a candidate for deletion; one with 3 accounts
  // and $500k behind it is load-bearing.
  const accounts = treeQ.data?.accounts ?? [];
  const mappedCount = new Map<string, number>();
  const mappedAmount = new Map<string, number>();
  for (const a of accounts) {
    if (!a.line_code) continue;
    mappedCount.set(a.line_code, (mappedCount.get(a.line_code) ?? 0) + 1);
    mappedAmount.set(a.line_code, (mappedAmount.get(a.line_code) ?? 0) + a.amount);
  }

  async function refresh() {
    invalidateCatalogLabels();
    catQ.refetch();
    treeQ.refetch();
  }

  async function patch(code: string, body: Parameters<typeof api.fpa.updateCatalogLine>[1], note: string) {
    const ok = await save.run(() => api.fpa.updateCatalogLine(code, body), note);
    if (ok) await refresh();
  }

  /**
   * Why a drop is or is not allowed, as a sentence rather than a silent refusal.
   * Returns null when it is fine.
   */
  function dropBlocked(srcCode: string, targetCode: string, where: DropWhere): string | null {
    const src = byCode.get(srcCode); const target = byCode.get(targetCode);
    if (!src || !target) return 'that line is no longer here';
    if (srcCode === targetCode) return null;
    // Into its own subtree would detach the branch from the tree entirely.
    // Uses the set computed once per render: this runs for every visible row
    // on every dragover, and walking the subtree each time is quadratic.
    const inSubtree = srcCode === dragCode && dragDescendants
      ? dragDescendants.has(targetCode)
      : descendantCodes(src).includes(targetCode);
    if (inSubtree) return `"${target.label}" sits inside "${src.label}"`;

    const newParent = where === 'inside' ? target.code : (target.parent_code ?? null);
    if (where === 'inside' && target.kind === 'leaf') {
      return `"${target.label}" holds figures, not lines — use Make group on it first`;
    }
    if (where !== 'inside' && newParent) {
      const p = byCode.get(newParent);
      if (p && p.kind === 'leaf') return `"${p.label}" holds figures, not lines`;
    }
    if ((src.parent_code ?? null) !== newParent) {
      if (ENGINE_OWNED.has(srcCode)) return `"${src.label}" is calculated by the engine and cannot be reparented`;
    }
    return null;
  }

  /**
   * Apply a drop: rewrite the destination level's order with the dragged line
   * inserted at the right index.
   *
   * The whole level goes in one request, because moving one line renumbers its
   * new siblings and a half-applied reorder leaves an order nobody chose.
   */
  async function applyDrop(srcCode: string, targetCode: string, where: DropWhere) {
    const blocked = dropBlocked(srcCode, targetCode, where);
    if (blocked) { setDropErr(blocked); return; }
    const src = byCode.get(srcCode)!; const target = byCode.get(targetCode)!;
    const newParent = where === 'inside' ? target.code : (target.parent_code ?? null);

    const siblings = flat
      .filter((n) => (n.parent_code ?? null) === newParent && n.code !== srcCode)
      .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
    let index = siblings.length;                       // 'inside' appends
    if (where !== 'inside') {
      const at = siblings.findIndex((n) => n.code === targetCode);
      index = at < 0 ? siblings.length : (where === 'before' ? at : at + 1);
    }
    const ordered = [...siblings.slice(0, index), src, ...siblings.slice(index)];
    const entries = ordered.map((n, k) => (
      n.code === srcCode
        ? { code: n.code, sort_order: k * 10, parent_code: newParent }
        : { code: n.code, sort_order: k * 10 }
    ));

    const ok = await save.run(() => api.fpa.reorderCatalog(entries),
      where === 'inside' ? `"${src.label}" moved into "${target.label}"` : `"${src.label}" moved`);
    if (ok) {
      // Opening the destination means the result is visible rather than
      // swallowed by a collapsed branch, which reads as the drop failing.
      if (newParent && effectiveCollapsed.has(newParent)) {
        const next = new Set(effectiveCollapsed); next.delete(newParent); persistCollapsed(next);
      }
      await refresh();
    }
  }

  /** Move a line among its siblings by rewriting the whole level's order. */
  async function move(node: TreeNode, dir: -1 | 1) {
    const siblings = flat.filter((n) => (n.parent_code ?? null) === (node.parent_code ?? null));
    const i = siblings.findIndex((n) => n.code === node.code);
    const j = i + dir;
    if (j < 0 || j >= siblings.length) return;
    const reordered = siblings.slice();
    [reordered[i], reordered[j]] = [reordered[j]!, reordered[i]!];
    const entries = reordered.map((n, k) => ({ code: n.code, sort_order: k * 10 }));
    const ok = await save.run(() => api.fpa.reorderCatalog(entries), 'order saved');
    if (ok) await refresh();
  }

  /** Nest under the sibling above, which is the only unambiguous "indent". */
  async function indent(node: TreeNode) {
    const siblings = flat.filter((n) => (n.parent_code ?? null) === (node.parent_code ?? null));
    const i = siblings.findIndex((n) => n.code === node.code);
    const prev = siblings[i - 1];
    if (!prev) { save.fail('Nothing above it at this level to nest under.'); return; }
    if (prev.kind === 'leaf') {
      save.fail(`"${prev.label}" is a leaf. Make it a group first, or move this line somewhere else.`);
      return;
    }
    const ok = await save.run(() => api.fpa.reorderCatalog([{ code: node.code, sort_order: 999, parent_code: prev.code }]), `nested under ${prev.label}`);
    if (ok) await refresh();
  }

  /** Promote to its grandparent's level. */
  async function outdent(node: TreeNode) {
    if (!node.parent_code) { save.fail('Already at the top level.'); return; }
    const parent = flat.find((n) => n.code === node.parent_code);
    const ok = await save.run(() => api.fpa.reorderCatalog([{ code: node.code, sort_order: 999, parent_code: parent?.parent_code ?? null }]), 'moved out one level');
    if (ok) await refresh();
  }

  // A leaf carrying money cannot become a parent, which is what 'Services
  // Income' needs to be to hold Time & Materials and Fixed-Fee while still
  // receiving its own actuals. One action, so the catalog is never invalid
  // half-way through.
  async function makeGroup(node: TreeNode) {
    if (!window.confirm(
      `Turn "${node.label}" into a group?\n\n`
      + `Its budget items and QBO mappings move to a new child line, "${node.label} (direct)", `
      + 'because a group cannot hold figures itself. You can then nest other lines under it.',
    )) return;
    const ok = await save.run(async () => {
      const out = await api.fpa.convertLineToGroup(node.code);
      return out;
    }, `"${node.label}" is now a group`);
    if (ok) await refresh();
  }

  async function repairStructure() {
    const dry = await api.fpa.repairLineStructure(true).catch((e) => { save.fail((e as Error).message); return null; });
    if (!dry) return;
    if (dry.promoted.length === 0) { save.markSaved('structure is already sound'); return; }
    const msg = [
      `${dry.promoted.length} line(s) hold other lines but are marked as holding figures.`,
      'The budget grid leaves such a branch out entirely, so the hierarchy under them is invisible.',
      '',
      ...dry.promoted.slice(0, 15).map((r) => `  ${r.label} (${r.children} child lines)`),
      '',
      'Promote them? Anything currently sitting on one moves to a new "(direct)" child.',
    ].join('\n');
    if (!window.confirm(msg)) return;
    let failed: Array<{ label: string; error: string }> = [];
    const ok = await save.run(async () => {
      failed = (await api.fpa.repairLineStructure(false)).failed;
    }, `${dry.promoted.length - 0} line(s) promoted`);
    if (!ok) return;
    await refresh();
    // A partial run still promoted what it could, so refresh before saying
    // which ones need a hand.
    if (failed.length) {
      save.fail(`${failed.length} could not be promoted: `
        + failed.map((f) => `${f.label} (${f.error})`).join('; '));
    }
  }

  // Previewed first: retiring a hundred lines on one click, with no list of
  // which, is not something anyone should be able to do by accident.
  async function retireUnused() {
    const dry = await api.fpa.retireUnusedLines(true).catch((e) => { save.fail((e as Error).message); return null; });
    if (!dry) return;
    if (dry.retired.length === 0) { save.markSaved('nothing unused to retire'); return; }
    const lines = [
      `Retire ${dry.retired.length} line(s) that nothing points at?`,
      '',
      ...dry.retired.slice(0, 15).map((r) => `  ${r.label}`),
      ...(dry.retired.length > 15 ? [`  …and ${dry.retired.length - 15} more`] : []),
      '',
      'History is kept; they just leave every picker. Restore any of them with "show retired".',
    ];
    if (!window.confirm(lines.join('\n'))) return;
    const ok = await save.run(() => api.fpa.retireUnusedLines(false), `${dry.retired.length} line(s) retired`);
    if (ok) await refresh();
  }

  async function remove(node: TreeNode) {
    if (!window.confirm(`Delete "${node.label}"? Only possible if nothing references it.`)) return;
    const ok = await save.run(async () => {
      const out = await api.fpa.deleteCatalogLine(node.code);
      if (!out.deleted) setBlockers({ code: node.code, blockers: out.blockers ?? [] });
    }, 'deleted');
    if (ok) await refresh();
  }

  const cell: React.CSSProperties = { padding: '3px 8px', fontSize: 11, borderBottom: '0.5px solid var(--color-border)' };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };
  const money = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <Card>
      <CardHeader>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>FP&amp;A line hierarchy</div>
          <span style={muted}>
            the shape of every statement in the product — rename, reorder, nest, and set which subtotal each line rolls into
          </span>
        </div>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <SaveStatus state={save} />
          <label style={{ ...muted, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            show retired
          </label>
          <ActionButton onClick={() => persistCollapsed(new Set(groupCodes))}
            title="Close every branch. Dragging needs the row and its destination on screen together, so closing what you are not working in is how a move across the statement becomes possible.">
            Collapse all
          </ActionButton>
          <ActionButton onClick={() => persistCollapsed(new Set())}>Expand all</ActionButton>
          {isApprover && <ActionButton onClick={() => setAdding({ parent: null })}>+ Top-level line</ActionButton>}
          {isApprover && (
            <ActionButton onClick={repairStructure}
              title="Promote any line that has ended up holding other lines but is still marked as holding figures. The budget grid leaves such a branch out entirely, so an imported hierarchy looks absent.">
              Repair structure
            </ActionButton>
          )}
          {isApprover && (
            <ActionButton onClick={retireUnused}
              title="Retire every line that no QuickBooks account maps to and no budget item sits on. Re-mirroring with a different shape leaves the previous shape's lines behind, pointed at by nothing.">
              Retire unused…
            </ActionButton>
          )}
          {isApprover && (
            <ActionButton onClick={() => setMirror(true)}
              title="Build the hierarchy from the QuickBooks chart of accounts, using its names and its nesting. Always previewed first.">
              Build from QuickBooks…
            </ActionButton>
          )}
        </span>
      </CardHeader>

      {!isApprover && (
        <p style={{ ...muted, padding: '8px 16px', margin: 0 }}>
          Read-only: restructuring the catalog is approver work.
        </p>
      )}

      {isApprover && (
        <p style={{ ...muted, padding: '0 16px 8px', margin: 0 }}>
          Drag a row by its handle. Dropping on the <b>middle</b> of a group puts the line inside it;
          dropping near the <b>top or bottom edge</b> of any row places it above or below that row.
          Close the branches you are not using so the destination is on screen — the view scrolls on its
          own near the window edges.
        </p>
      )}

      {dropErr && (
        <p style={{ fontSize: 11, color: 'var(--color-red-text)', padding: '0 16px 8px', margin: 0 }}>
          {dropErr}
        </p>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Line</th>
              <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Rolls into</th>
              <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>Code</th>
              <th style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>QBO accts</th>
              <th style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>Actuals</th>
              <th style={{ ...cell, fontWeight: 600 }} />
            </tr>
          </thead>
          <tbody>
            {visible.map((n) => {
              const locked = ENGINE_OWNED.has(n.code);
              // Calculated from drivers, but free to be renamed and moved.
              const fedBy = ENGINE_FED[n.code];
              const isGroup = n.kind === 'group';
              const hasKids = n.children.length > 0;
              const shut = effectiveCollapsed.has(n.code);
              const dragging = dragCode === n.code;
              const at = dropAt?.code === n.code ? dropAt.where : null;
              const bad = dragCode ? dropBlocked(dragCode, n.code, at ?? 'after') : null;
              // The indicator says exactly what will happen: a line between
              // siblings, or the row itself ringed for 'inside'. Red when the
              // drop is refused, with the reason in the bar below the table.
              const edge = bad ? 'var(--color-red-text)' : 'var(--color-blue)';
              return (
                <tr key={n.code}
                  draggable={isApprover && !dragDisabled(n)}
                  onDragStart={(e) => {
                    setDragCode(n.code); setDropErr(null);
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox needs something set or the drag never starts.
                    e.dataTransfer.setData('text/plain', n.code);
                  }}
                  onDragEnd={() => { setDragCode(null); setDropAt(null); }}
                  onDragOver={(e) => {
                    if (!dragCode || dragCode === n.code) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    autoScroll(e.clientY);
                    setDropAt({ code: n.code, where: dropIntent(e, isGroup || hasKids) });
                  }}
                  onDragLeave={() => setDropAt((d) => (d?.code === n.code ? null : d))}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = dragCode; const where = dropAt?.where ?? 'after';
                    setDragCode(null); setDropAt(null);
                    if (src && src !== n.code) void applyDrop(src, n.code, where);
                  }}
                  style={{
                    opacity: n.is_active ? (dragging ? 0.4 : 1) : 0.5,
                    cursor: isApprover && !dragDisabled(n) ? 'grab' : undefined,
                    borderTop: at === 'before' ? `2px solid ${edge}` : undefined,
                    borderBottom: at === 'after' ? `2px solid ${edge}` : undefined,
                    outline: at === 'inside' ? `2px solid ${edge}` : undefined,
                    outlineOffset: at === 'inside' ? '-2px' : undefined,
                    background: at === 'inside' && !bad ? 'rgba(80,140,255,0.10)' : undefined,
                  }}>
                  <td style={{ ...cell, paddingLeft: 8 + n.depth * 16 }}>
                    {isApprover && !dragDisabled(n) && (
                      <span style={{ ...muted, cursor: 'grab', marginRight: 4 }} title="Drag to move this line">⠇</span>
                    )}
                    {hasKids
                      ? <button onClick={() => toggle(n.code)} title={shut ? 'Open' : 'Close'}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 10, padding: '0 4px 0 0' }}>
                          {shut ? '▸' : '▾'}
                        </button>
                      : <span style={{ display: 'inline-block', width: 14 }} />}
                    {isApprover && !locked
                      ? <InlineLabel value={n.label} onSave={(v) => patch(n.code, { label: v }, 'renamed')} bold={isGroup} />
                      : <span style={{ fontWeight: isGroup || locked ? 600 : 400 }}>{n.label}</span>}
                    {locked && <span style={{ ...muted, marginLeft: 6 }} title="Calculated by the engine. Rename and reorder it freely; its classification and position in the maths are fixed.">🔒 calculated</span>}
                    {fedBy && (
                      <span style={{ ...muted, marginLeft: 6 }}
                        title={`Calculated from ${fedBy}. Rename it and move it wherever it belongs; it cannot be reclassified, retired or deleted, because the engine keeps writing to it.`}>
                        ⚙ from {fedBy.split(':')[0]}
                      </span>
                    )}
                    {n.kind === 'group' && <span style={{ ...muted, marginLeft: 6 }}>group</span>}
                    {shut && hasKids && (
                      <span style={{ ...muted, marginLeft: 6 }}>{n.children.length} inside</span>
                    )}
                    {!n.is_active && <Badge variant="gray">retired</Badge>}
                  </td>
                  <td style={cell}>
                    {isApprover && !locked && !fedBy ? (
                      <select value={n.family} onChange={(e) => patch(n.code, { family: e.target.value as LineFamily }, 'reclassified')}
                        title={FAMILIES.find((f) => f.key === n.family)?.hint}
                        style={{ fontSize: 11, padding: '1px 4px', background: 'var(--color-surface)', color: 'var(--color-text)', border: '0.5px solid var(--color-border)', borderRadius: 3 }}>
                        {FAMILIES.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        {!FAMILIES.some((f) => f.key === n.family) && <option value={n.family}>{FAMILY_SHORT[n.family] ?? n.family}</option>}
                      </select>
                    ) : <span style={muted}>{FAMILY_SHORT[n.family] ?? n.family}</span>}
                  </td>
                  <td style={{ ...cell, ...muted, fontFamily: 'var(--font-mono, monospace)' }}>{n.code}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{mappedCount.get(n.code) ?? ''}</td>
                  <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {mappedAmount.get(n.code) ? money(mappedAmount.get(n.code)!) : ''}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {isApprover && (
                      <>
                        <ActionButton onClick={() => move(n, -1)} title="Move up among its siblings">↑</ActionButton>{' '}
                        <ActionButton onClick={() => move(n, 1)} title="Move down among its siblings">↓</ActionButton>{' '}
                        {!locked && <><ActionButton onClick={() => indent(n)} title="Nest under the line above">→</ActionButton>{' '}</>}
                        {!locked && <><ActionButton onClick={() => outdent(n)} title="Move out one level">←</ActionButton>{' '}</>}
                        {isGroup && <><ActionButton onClick={() => setAdding({ parent: n })} title="Add a line under this group">+</ActionButton>{' '}</>}
                        {!isGroup && !locked && !fedBy && (
                          <><ActionButton onClick={() => makeGroup(n)}
                            title="Turn this leaf into a group so other lines can nest under it. Its items and mappings move to a new '(direct)' child, because a group cannot hold figures itself.">
                            Make group
                          </ActionButton>{' '}</>
                        )}
                        {!locked && !fedBy && n.is_active && <><ActionButton onClick={() => patch(n.code, { is_active: false }, 'retired')} title="Retire: keeps the history, removes it from every picker">Retire</ActionButton>{' '}</>}
                        {!locked && !fedBy && !n.is_active && <><ActionButton onClick={() => patch(n.code, { is_active: true }, 'restored')}>Restore</ActionButton>{' '}</>}
                        {!locked && !fedBy && <ActionButton variant="danger" onClick={() => remove(n)} title="Delete, if nothing references it">×</ActionButton>}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ ...muted, padding: '0 16px 10px', margin: 0 }}>
        ⚙ marks a line the engine calculates from drivers rather than from budget figures or
        a QuickBooks account. Rename it and move it wherever it belongs; it cannot be reclassified
        or removed, because the engine keeps writing to it. 🔒 marks a subtotal, derived from
        whatever sits above it.
      </p>
      {flat.length === 0 && <EmptyState message="No lines yet — build the hierarchy from QuickBooks, or add a top-level line." />}

      {adding && <AddLine parent={adding.parent} siblings={flat} onClose={() => setAdding(null)}
        onSaved={async (note) => { setAdding(null); save.markSaved(note); await refresh(); }} />}

      {mirror && <MirrorDialog onClose={() => setMirror(false)}
        onApplied={async (note) => { setMirror(false); save.markSaved(note); await refresh(); }} />}

      {blockers && (
        <Modal title="Cannot delete that line" subtitle={blockers.code} onClose={() => setBlockers(null)} width={520}>
          <div style={{ padding: 18, fontSize: 12 }}>
            <p style={{ marginTop: 0 }}>Something still depends on it:</p>
            <ul style={{ paddingLeft: 18 }}>
              {blockers.blockers.map((b) => <li key={b.kind} style={{ marginBottom: 4 }}>{b.detail}</li>)}
            </ul>
            <p style={muted}>
              <strong>Retire</strong> it instead: the history stays readable and the line disappears from every
              picker. That is almost always what is wanted — deleting it would leave calculated rows and QBO
              mappings pointing at a code with nothing to render it.
            </p>
            <ActionButton onClick={() => setBlockers(null)}>Close</ActionButton>
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ─── Inline rename ──────────────────────────────────────────────────────────

function InlineLabel({ value, onSave, bold }: { value: string; onSave: (v: string) => void; bold?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  if (!editing) {
    return (
      <span onClick={() => { setDraft(value); setEditing(true); }} title="Click to rename"
        style={{ cursor: 'text', fontWeight: bold ? 600 : 400, borderBottom: '1px dotted var(--color-border-strong)' }}>
        {value}
      </span>
    );
  }
  const commit = () => { setEditing(false); if (draft.trim() && draft !== value) onSave(draft.trim()); };
  return (
    <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      style={{ fontSize: 11, padding: '1px 4px', width: 240, background: 'var(--color-surface)', color: 'var(--color-text)', border: '0.5px solid var(--color-border-strong)', borderRadius: 3 }} />
  );
}

// ─── Add a line ─────────────────────────────────────────────────────────────

function AddLine({ parent, siblings, onClose, onSaved }: {
  parent: CatalogLineRow | null; siblings: CatalogLineRow[];
  onClose: () => void; onSaved: (note: string) => void;
}) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<'leaf' | 'group'>('leaf');
  const [family, setFamily] = useState<LineFamily>((parent?.family as LineFamily) ?? 'opex');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Codes are permanent and dot-namespaced, so suggest one from the label and
  // the parent rather than making the operator invent the convention.
  const slug = (s: string) => s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  const suggested = `${parent ? `${parent.code}.` : ''}${slug(label) || 'new_line'}`;
  const finalCode = (code.trim() || suggested);

  async function submit() {
    if (!label.trim()) { setErr('A name is required.'); return; }
    if (siblings.some((s) => s.code === finalCode)) { setErr(`The code ${finalCode} is already in use.`); return; }
    setBusy(true); setErr(null);
    try {
      await api.fpa.createCatalogLine({ code: finalCode, label: label.trim(), parent_code: parent?.code ?? null, family, kind });
      onSaved(`"${label.trim()}" added`);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  const box: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
  const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
  const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 96 };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };

  return (
    <Modal title={parent ? `Add a line under ${parent.label}` : 'Add a top-level line'} onClose={onClose} width={560}>
      <div style={{ padding: 18 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}
        <div style={row}>
          <span style={lab}>Name</span>
          <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Commission Earned - Hubspot" style={{ ...box, flex: 1 }} />
        </div>
        <div style={row}>
          <span style={lab}>Holds</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as 'leaf' | 'group')} style={{ ...box, flex: 1 }}>
            <option value="leaf">Budget figures (a leaf)</option>
            <option value="group">Other lines (a group)</option>
          </select>
        </div>
        <p style={{ ...muted, margin: '-6px 0 10px 104px' }}>
          Budget items and QBO mappings can only sit on a leaf. A group only totals what is under it.
        </p>
        <div style={row}>
          <span style={lab}>Rolls into</span>
          <select value={family} onChange={(e) => setFamily(e.target.value as LineFamily)} style={{ ...box, flex: 1 }}>
            {FAMILIES.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
        <p style={{ ...muted, margin: '-6px 0 10px 104px' }}>{FAMILIES.find((f) => f.key === family)?.hint}</p>
        <div style={row}>
          <span style={lab}>Code</span>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={suggested} style={{ ...box, flex: 1, fontFamily: 'var(--font-mono, monospace)' }} />
        </div>
        <p style={{ ...muted, margin: '-6px 0 14px 104px' }}>
          Permanent once saved, and what every budget item and mapping stores. Leave blank for <code>{suggested}</code>.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton variant="primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Add line'}</ActionButton>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
        </div>
      </div>
    </Modal>
  );
}

// ─── Build from QuickBooks ──────────────────────────────────────────────────

/**
 * Mirror the chart of accounts into the catalog. Preview first, always: the
 * result is the shape of every statement, and an import that restructured the
 * catalog on one click would be unrecoverable.
 */
function MirrorDialog({ onClose, onApplied }: { onClose: () => void; onApplied: (note: string) => void }) {
  const accountsQ = useQuery(() => api.fpa.qbo.accountTree(true), []);
  const deptsQ = useQuery(() => api.fpa.departments(), []);
  const [aliasBusy, setAliasBusy] = useState<string | null>(null);
  const [root, setRoot] = useState('');
  /**
   * OFF by default. On, an account that is already mapped is skipped - so on
   * a chart that is already fully mapped, re-running this moves almost
   * nothing: the new shared lines pick up only the few unmapped accounts,
   * every old one-per-account line keeps its account, and 'Retire unused' can
   * then never clear them because they are not unused. Rebuilding the shape
   * means re-pointing the accounts, and the preview shows every move first.
   */
  const [keepExisting, setKeepExisting] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const [exclude, setExclude] = useState('XXX');
  const [plan, setPlan] = useState<MirrorPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const roots = (accountsQ.data?.accounts ?? []).filter((a) => a.has_children);

  /**
   * Teach one wording and re-preview straight away.
   *
   * Re-previewing is the point: the operator needs to SEE the accounts leave
   * the unmatched list and join a shared line, because the alternative is
   * applying on faith and finding out afterwards.
   */
  async function teachAlias(word: string, departmentId: string) {
    if (!departmentId) return;
    setAliasBusy(word); setErr(null);
    try {
      await api.fpa.setDepartmentAlias(word, departmentId);
      await run(false);
    } catch (e) { setErr((e as Error).message); }
    finally { setAliasBusy(null); }
  }

  async function run(apply: boolean) {
    setBusy(true); setErr(null);
    try {
      const body = {
        root_account_id: root || null, keep_existing_maps: keepExisting,
        only_with_activity: onlyActive,
        exclude_name_contains: exclude.trim() || null,
      };
      if (apply) {
        const out = await api.fpa.mirrorQboApply(body);
        onApplied(`${out.lines_created} line(s) created, ${out.maps_set} mapping(s) set`
          + (out.departments_set ? `, ${out.departments_set} department(s) assigned` : ''));
      } else {
        setPlan((await api.fpa.mirrorQboPreview(body)).plan);
      }
    } catch (e) { setErr((e as Error).message); if (!apply) setPlan(null); }
    finally { setBusy(false); }
  }

  const box: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
  const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
  const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 110 };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };

  return (
    <Modal title="Build the hierarchy from QuickBooks" subtitle="uses the chart of accounts' own names and nesting" onClose={onClose} width={760}>
      <div style={{ padding: 18 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}

        <p style={{ ...muted, margin: '0 0 12px' }}>
          An account named <b>Type — Department</b> is split: “Bonuses - Sales” becomes the line
          <b> Bonuses</b> with the department <b>Sales</b>. One Bonuses row then totals every
          department and the department selector breaks it out — QuickBooks nests department-first,
          so a cross-department total has no account of its own to live on. Accounts whose name does
          not split, or whose trailing word is not one of your departments, get a line each.
        </p>

        <div style={row}>
          <span style={lab}>Leave out</span>
          <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={onlyActive} onChange={(e) => { setOnlyActive(e.target.checked); setPlan(null); }} />
            accounts with no activity
          </label>
          <span style={muted}>names containing</span>
          <input value={exclude} onChange={(e) => { setExclude(e.target.value); setPlan(null); }} placeholder="XXX" style={{ ...box, width: 90 }} />
        </div>
        <p style={{ ...muted, margin: '-6px 0 12px 118px' }}>
          A chart accumulates accounts nobody posts to, and retires others by renaming them. Both bury
          the lines that carry money.
        </p>

        <div style={row}>
          <span style={lab}>Branch</span>
          <select value={root} onChange={(e) => { setRoot(e.target.value); setPlan(null); }} style={{ ...box, flex: 1 }}>
            <option value="">Everything on the P&amp;L</option>
            {roots.map((a) => (
              <option key={a.qbo_account_id} value={a.qbo_account_id}>
                {' '.repeat(a.depth * 2)}{a.name}
              </option>
            ))}
          </select>
        </div>

        <div style={row}>
          <span style={lab}>Existing maps</span>
          <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={keepExisting} onChange={(e) => { setKeepExisting(e.target.checked); setPlan(null); }} />
            leave accounts that are already mapped where they point
          </label>
        </div>
        <p style={{ ...muted, margin: '-6px 0 12px 118px' }}>
          Left unchecked, every account is re-pointed at its mirrored line, and the preview lists what each
          one was mapped to before so nothing moves unseen. <b>Check it only to add to the current shape
          rather than rebuild it</b>: on an already-mapped chart it skips nearly everything, which leaves the
          old lines holding their accounts and out of reach of “Retire unused”.
        </p>

        <div style={{ ...row, marginBottom: 14 }}>
          <span style={lab} />
          <ActionButton onClick={() => run(false)} disabled={busy}>{busy ? 'Reading…' : 'Preview'}</ActionButton>
          {plan && <ActionButton variant="primary" onClick={() => run(true)} disabled={busy}>Create {plan.lines.filter((l) => !l.exists).length} line(s) &amp; set {plan.maps.length} mapping(s)</ActionButton>}
        </div>

        {plan && !plan.hierarchy_synced && (
          <div style={{ fontSize: 11, padding: '8px 10px', marginBottom: 12, borderRadius: 'var(--radius-sm)', background: 'rgba(240,180,0,0.14)' }}>
            <strong>No account in the chart has a parent</strong>, so the QuickBooks hierarchy has not been
            synced yet and this mirror will produce a flat list under each top-level group. Run a
            QuickBooks sync first — the hierarchy columns are only filled in by a sync, and the
            migration that added them leaves them empty.
          </div>
        )}

        {plan && plan.skipped.filter((s) => s.reason.startsWith('already mapped')).length > 0 && (
          <div style={{ fontSize: 11, padding: '8px 10px', marginBottom: 12, borderRadius: 'var(--radius-sm)', background: 'rgba(240,180,0,0.14)' }}>
            <strong>{plan.skipped.filter((s) => s.reason.startsWith('already mapped')).length} accounts are being left
            where they are</strong> because they are already mapped. They will keep pointing at their current
            lines, those lines will not become unused, and “Retire unused” will not be able to clear them.
            Uncheck <em>leave accounts that are already mapped</em> above to rebuild properly.
          </div>
        )}

        {plan && plan.type_groups.length > 0 && (
          <div style={{ fontSize: 11, marginBottom: 12 }}>
            <p style={{ ...muted, margin: '0 0 4px', fontWeight: 600 }}>Expense types found</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {plan.type_groups.map((g) => (
                <span key={g.line_code} title={`${g.line_code}\n${g.departments.join(', ')}`}
                  style={{ padding: '1px 6px', borderRadius: 3, border: '0.5px solid var(--color-border)' }}>
                  {g.type} <span style={muted}>{g.accounts} accts / {g.departments.length} dept</span>
                </span>
              ))}
            </div>
            <p style={{ ...muted, margin: '6px 0 0' }}>
              Spellings that differ only by a plural are one type ("Bonus" joins "Bonuses"). Anything here
              that should be merged can be remapped on the Accounts tab afterwards.
            </p>
          </div>
        )}

        {plan && plan.unmatched_departments.length > 0 && (
          <div style={{ fontSize: 11, marginBottom: 12 }}>
            <p style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--color-amber)' }}>
              Not recognised as departments
            </p>
            <p style={{ ...muted, margin: '0 0 6px' }}>
              These accounts are named "Type — Something", but the Something matches no FP&amp;A department,
              so each keeps a line of its own. Point it at the department it means and these accounts will
              collapse onto a shared line — the preview refreshes so you can see it happen. The department
              list is not renamed; the wording is just taught as an equivalent.
            </p>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                {plan.unmatched_departments.slice(0, 20).map((u) => (
                  <tr key={u.word}>
                    <td style={{ padding: '2px 6px', whiteSpace: 'nowrap' }}><strong>{u.word}</strong></td>
                    <td style={{ padding: '2px 6px', ...muted }} title={u.accounts.join('\n')}>
                      {u.accounts.length} account{u.accounts.length === 1 ? '' : 's'}
                    </td>
                    <td style={{ padding: '2px 6px' }}>
                      <select defaultValue="" disabled={aliasBusy === u.word}
                        onChange={(e) => teachAlias(u.word, e.target.value)}
                        style={{ ...box, fontSize: 11, padding: '1px 4px' }}>
                        <option value="">{aliasBusy === u.word ? 'saving…' : 'means…'}</option>
                        {(deptsQ.data?.departments ?? []).map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '2px 6px', ...muted }}>
                      {u.accounts.slice(0, 2).join(', ')}{u.accounts.length > 2 ? ', …' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {plan.unmatched_departments.length > 20 && (
              <p style={{ ...muted, margin: '4px 0 0' }}>…and {plan.unmatched_departments.length - 20} more.</p>
            )}
          </div>
        )}

        {plan && (
          <div style={{ fontSize: 11, maxHeight: 420, overflowY: 'auto' }}>
            <p style={{ ...muted, margin: '0 0 6px' }}>
              Hanging from {plan.roots_used.join(', ') || 'nothing'} · {plan.lines.filter((l) => !l.exists).length} new,
              {' '}{plan.lines.filter((l) => l.exists).length} already present · {plan.skipped.length} skipped
            </p>

            {plan.lines.length > 0 && (
              <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 12 }}>
                <thead><tr>
                  <th style={{ textAlign: 'left', ...muted, padding: '2px 6px' }}>New line</th>
                  <th style={{ textAlign: 'left', ...muted, padding: '2px 6px' }}>Under</th>
                  <th style={{ textAlign: 'left', ...muted, padding: '2px 6px' }}>Rolls into</th>
                </tr></thead>
                <tbody>
                  {plan.lines.map((l) => (
                    <tr key={l.code} style={{ opacity: l.exists ? 0.45 : 1 }}>
                      <td style={{ padding: '2px 6px' }}>
                        {l.label}
                        {l.direct_leaf && <span style={{ ...muted, marginLeft: 6 }} title="This QBO account is a parent that is also posted to directly, so its own charges need a leaf of their own — a group cannot hold money.">own postings</span>}
                        {l.exists && <span style={{ ...muted, marginLeft: 6 }}>already there</span>}
                      </td>
                      <td style={{ padding: '2px 6px', ...muted, fontFamily: 'var(--font-mono, monospace)' }}>{l.parent_code}</td>
                      <td style={{ padding: '2px 6px', ...muted }}>{FAMILY_SHORT[l.family] ?? l.family}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {plan.maps.length > 0 && (
              <>
                <p style={{ ...muted, margin: '0 0 4px', fontWeight: 600 }}>Mappings</p>
                <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 12 }}>
                  <tbody>
                    {plan.maps.map((m) => (
                      <tr key={m.qbo_account_id}>
                        <td style={{ padding: '2px 6px' }}>{m.name}</td>
                        <td style={{ padding: '2px 6px', ...muted }}>
                          {m.was ? <>was <code>{m.was}</code> → </> : null}<code>{m.line_code}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {plan.department_maps.length > 0 && (
              <>
                <p style={{ ...muted, margin: '0 0 4px', fontWeight: 600 }}>Departments assigned</p>
                <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 12 }}>
                  <tbody>
                    {plan.department_maps.map((d) => (
                      <tr key={d.qbo_account_id}>
                        <td style={{ padding: '2px 6px' }}>{d.name}</td>
                        <td style={{ padding: '2px 6px', ...muted }}>
                          {d.was ? <>was <code>{d.was}</code> → </> : null}<code>{d.department_id}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {plan.skipped.length > 0 && (
              <>
                <p style={{ ...muted, margin: '0 0 4px', fontWeight: 600 }}>Skipped</p>
                <ul style={{ ...muted, paddingLeft: 18, margin: 0 }}>
                  {plan.skipped.slice(0, 30).map((s) => <li key={s.qbo_account_id}>{s.name} — {s.reason}</li>)}
                </ul>
                {plan.skipped.length > 30 && (
                  <p style={{ ...muted, margin: '4px 0 0' }}>…and {plan.skipped.length - 30} more.</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
